import React, { ReactNode } from 'react';
import { Box } from '@cp/ds/src/components/box';
import { Stack } from '@cp/ds/src/components/stack';
import { useIsMobile } from '@cp/ds/src/hooks/useIsMobile';
import { MOBILE_BREAKPOINT_THRESHOLD, SxProps } from '@cp/ds/src/theme';
import { ContentSkeleton } from '@cp/ds/src/components/contentSkeleton';
import { Typography } from '@cp/ds/src/components/typography';
import { Container } from '@cp/ds/src/components/container';
import {
  ResumeAdditional,
  ResumeAuthorAge,
  ResumeAuthorLocationLink,
  ResumeAuthorName,
  ResumeCardTitle,
  ResumeExperienceLabel,
} from '@cp/entities/resume';
import { CatalogItemViewModel } from '@common/model/catalog/item/view';
import { ClockIcon } from '@cp/ds/src/icons';
import { LocationUrlProvider } from '@cp/shared/location';
import { AuthorRegistrationDate } from '@cp/entities/resume/ui/authorRegistrationDate';
import { ClientPhotoPurpose } from '@common/model/client/photo/purpose';
import { SafetyLabels } from 'src/entities/resume';
import { useHasAdditionalParameters } from '../../model';
import ResumeCard from '../card';
import { Schedule } from '../schedule';
import { Tariffs } from '../tariffs';
import { ResumeCardStats } from '../card/stats';
import { Price } from '../price';

const CARD_WIDTH_DESKTOP = 334;
const CARD_WIDTH_MOBILE = '100%';
const buildGoogleMapsURL: LocationUrlProvider = (p) => new URL(`https://www.google.com/maps/?q=${p}`);

export interface ResumeProps {
  data: CatalogItemViewModel | undefined;
  isLoading?: boolean;
  children?: ReactNode | ReactNode[];
  secondaryActionButton?: ReactNode;
  sx?: SxProps;
  favoriteButton?: React.ReactNode;
  showExtraAgeLabel?: boolean;
  showExperienceLabel?: boolean;
  careerLabel?: ReactNode;
  map?: ReactNode;
  onlineStatusLabel?: ReactNode;
  feedbacks?: ReactNode;
  showAdditional?: boolean;
  advantages?: ReactNode[] | ReactNode;
}

export const Resume: React.FC<ResumeProps> = ({
  data,
  isLoading,
  sx,
  secondaryActionButton,
  favoriteButton,
  showExtraAgeLabel,
  showExperienceLabel,
  children,
  careerLabel,
  map,
  onlineStatusLabel,
  feedbacks,
  showAdditional,
  advantages,
}) => {
  const isMobile = useIsMobile();
  const hasAdditionalParameters = useHasAdditionalParameters(data);

  const SectionContainer = isMobile ? Container : Box;

  if (isLoading) {
    return (
      <SectionContainer>
        <ContentSkeleton blocks={2} spacing={1} />
      </SectionContainer>
    );
  }

  if (!data) {
    return null;
  }

  const renderSection = ({ title, content }: { title: React.ReactNode; content: React.ReactNode }) => {
    return (
      <SectionContainer>
        <Typography component="div" sx={{ mb: 1.5 }} variant="h3">
          {title}
        </Typography>
        {content}
      </SectionContainer>
    );
  };

  return (
    <Stack direction="column" gap={{ xs: 2.5, [MOBILE_BREAKPOINT_THRESHOLD]: 3 }} sx={sx}>
      <Stack
        direction={{ xs: 'column', [MOBILE_BREAKPOINT_THRESHOLD]: 'row' }}
        gap={2.5}
        sx={{ alignItems: 'flex-start', alignSelf: 'self-start', flex: '1 1 0px', maxWidth: '100%' }}
      >
        <ResumeCard
          clientProfilePhotoPurpose={ClientPhotoPurpose.Resume}
          data={data}
          favoriteButton={isMobile && favoriteButton}
          footer={<ResumeCardStats careerId={data.careerId} data={data} />}
          sx={{ borderRadius: isMobile ? 'unset' : undefined, backgroundColor: 'white' }}
          width={isMobile ? CARD_WIDTH_MOBILE : CARD_WIDTH_DESKTOP}
        />
        <SectionContainer sx={{ flex: '1 1 0px', gap: 2.5, alignSelf: 'stretch', display: 'flex', flexDirection: 'column' }}>
          <section>
            <ResumeCardTitle>
              <ResumeAuthorName author={data?.clientProfile || {}} variant="h2" />
              <ResumeAuthorAge age={data.clientProfile?.age} before={showExtraAgeLabel && 'возраст '} variant="body bold" />
            </ResumeCardTitle>
            <Box sx={{ mt: 1, display: 'flex', gap: 1, alignItems: 'center', '&:empty': { display: 'none' } }}>
              {careerLabel}
              {onlineStatusLabel}
            </Box>
          </section>
          <AuthorRegistrationDate data={data} />
          {children}
        </SectionContainer>
      </Stack>

      {data.translation?.description
        ? renderSection({
            title: 'Обо мне',
            content: (
              <div>
                {showExperienceLabel && data.experience ? (
                  <ResumeExperienceLabel before={<ClockIcon />} experience={data.experience} />
                ) : null}
                <Typography component="pre" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }} variant="body">
                  {data.translation.description}
                </Typography>
              </div>
            ),
          })
        : null}

      {data.tariffs
        ? renderSection({ title: 'Цена услуги', content: <Tariffs data={data.tariffs} /> })
        : data.price && renderSection({ title: 'Цена услуги', content: <Price data={data.price} /> })}

      <SectionContainer>{secondaryActionButton}</SectionContainer>

      {showAdditional && hasAdditionalParameters && renderSection({ title: 'Дополнительно', content: <ResumeAdditional data={data} /> })}

      {renderSection({ title: 'Расписание', content: <Schedule data={data.schedule} sx={{ mt: 2.5 }} /> })}

      {renderSection({
        title: 'Адрес',
        content: (
          <section>
            <ResumeAuthorLocationLink data={data} provider={buildGoogleMapsURL} />
            {map}
          </section>
        ),
      })}

      {renderSection({
        title: 'Безопасность',
        content: (
          <section>
            <SafetyLabels isDocumentsChecked={data.clientProfile?.isDocumentsChecked} />
          </section>
        ),
      })}

      {Boolean(advantages) &&
        renderSection({
          title: 'Преимущества',
          content: <section>{advantages}</section>,
        })}

      {Boolean(feedbacks) && <SectionContainer>{feedbacks}</SectionContainer>}
    </Stack>
  );
};
Resume.displayName = 'entity/Resume';
